import { HttpErrorResponse } from '@angular/common/http';
import { SolText, SolValidationMessage } from '@sol/components';
import { AsmError, AsmErrorType } from '../models/asm-error';

export class AsmServiceTools {
  public static toAsmError(error: HttpErrorResponse, operation: string): AsmError {
    const type = AsmServiceTools.mapStatusToType(error.status);
    const messages = AsmServiceTools.parseErrorPayload(
      type,
      error.error,
      error.message || error.statusText || 'Request failed',
    );
    if (messages.length === 0) {
      const fallbackKey = AsmServiceTools.fallbackTranslationKey(type);
      messages.push({
        type: 'error',
        text: fallbackKey
          ? SolText.of(fallbackKey)
          : SolText.ofRaw(error.message || error.statusText || 'Request failed'),
      });
    }
    return AsmError.of(type, error.status, messages, operation);
  }

  /**
   * Normalizes any unknown runtime/API error to AsmError so components can
   * safely consume errors without custom guards.
   */
  public static toAsmErrorFromUnknown(error: unknown, operation: string): AsmError {
    if (error instanceof AsmError) {
      return error;
    }
    if (error instanceof HttpErrorResponse) {
      return AsmServiceTools.toAsmError(error, operation);
    }

    const status = AsmServiceTools.readStatus(error);
    const type = AsmServiceTools.mapStatusToType(status);
    const fallbackMessage = AsmServiceTools.readMessage(error);
    const payload = AsmServiceTools.readPayload(error);
    const messages = AsmServiceTools.parseErrorPayload(type, payload, fallbackMessage);

    if (messages.length === 0) {
      const fallbackKey = AsmServiceTools.fallbackTranslationKey(type);
      messages.push({
        type: 'error',
        text: fallbackKey ? SolText.of(fallbackKey) : SolText.ofRaw(fallbackMessage),
      });
    }

    return AsmError.of(type, status, messages, operation);
  }

  public static mapStatusToType(status: number): AsmErrorType {
    if (status === 0) {
      return AsmErrorType.NETWORK;
    }
    if (status === 400) {
      return AsmErrorType.BAD_REQUEST;
    }
    if (status === 401) {
      return AsmErrorType.UNAUTHORIZED;
    }
    if (status === 403) {
      return AsmErrorType.FORBIDDEN;
    }
    if (status === 404) {
      return AsmErrorType.NOT_FOUND;
    }
    if (status === 405) {
      return AsmErrorType.METHOD_NOT_ALLOWED;
    }
    if (status === 410) {
      return AsmErrorType.GONE;
    }
    if (status === 429) {
      return AsmErrorType.TOO_MANY_REQUESTS;
    }
    if (status === 503) {
      return AsmErrorType.SERVICE_UNAVAILABLE;
    }
    if (status >= 500) {
      return AsmErrorType.INTERNAL_SERVER_ERROR;
    }
    if (status >= 400) {
      return AsmErrorType.BAD_REQUEST;
    }
    return AsmErrorType.INTERNAL_SERVER_ERROR;
  }

  private static readonly FALLBACK_KEYS: Partial<Record<AsmErrorType, string>> = {
    [AsmErrorType.NETWORK]: 'asm-components.error.network',
    [AsmErrorType.FORBIDDEN]: 'asm-components.error.forbidden',
    [AsmErrorType.INTERNAL_SERVER_ERROR]: 'asm-components.error.server',
    [AsmErrorType.SERVICE_UNAVAILABLE]: 'asm-components.error.service-unavailable',
    [AsmErrorType.TOO_MANY_REQUESTS]: 'asm-components.error.too-many-requests',
  };

  private static fallbackTranslationKey(type: AsmErrorType): string | undefined {
    return AsmServiceTools.FALLBACK_KEYS[type];
  }

  /**
   * A request that never reached the server carries no server payload. The transport
   * decides what lands in it - a ProgressEvent under XHR, the raw TypeError under
   * fetch - and neither is worth showing, so the translated fallback wins instead.
   */
  private static parseErrorPayload(type: AsmErrorType, payload: any, fallbackMessage: string): SolValidationMessage[] {
    if (type === AsmErrorType.NETWORK) {
      return [];
    }
    return AsmServiceTools.parseApiMessages(payload, fallbackMessage);
  }

  public static parseDrfMessages(payload: any, fallbackMessage: string): SolValidationMessage[] {
    return AsmServiceTools.parseApiMessages(payload, fallbackMessage);
  }

  /**
   * Parses validation/error payloads from both Django/DRF style responses and
   * v2 API error formats.
   */
  public static parseApiMessages(payload: any, fallbackMessage: string): SolValidationMessage[] {
    if (payload === undefined || payload === null) {
      return [];
    }

    // Avoid exposing meaningless network payloads like { isTrusted: true }.
    if (
      typeof payload === 'object' &&
      !Array.isArray(payload) &&
      payload !== null &&
      Object.keys(payload as Record<string, unknown>).length === 1 &&
      (payload as Record<string, unknown>)['isTrusted'] === true
    ) {
      return [];
    }

    return AsmServiceTools.parseValue(undefined, payload, fallbackMessage);
  }

  private static parseValue(path: string | undefined, value: unknown, fallbackMessage: string): SolValidationMessage[] {
    if (value === undefined || value === null) {
      return [];
    }

    if (typeof value === 'string') {
      return [AsmServiceTools.toMessage(value, path)];
    }

    if (typeof value === 'number') {
      return [AsmServiceTools.toMessage(String(value), path)];
    }

    if (typeof value === 'boolean') {
      return [];
    }

    if (Array.isArray(value)) {
      const messages: SolValidationMessage[] = [];
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        // Per-index DRF validation: objects that are empty (placeholders) or contain
        // nested arrays/objects represent indexed structures like sections[2].title.
        // Objects with only primitive values are message wrappers (e.g. { message: '...' })
        // and keep the parent field path, same as plain strings in the array.
        const isIndexed =
          typeof item === 'object' &&
          item !== null &&
          (Object.keys(item as Record<string, unknown>).length === 0 ||
            Object.values(item as Record<string, unknown>).some(v => typeof v === 'object' && v !== null));
        const itemPath = isIndexed ? (path != null ? `${path}[${i}]` : `[${i}]`) : path;
        messages.push(...AsmServiceTools.parseValue(itemPath, item, fallbackMessage));
      }
      return messages;
    }

    if (typeof value !== 'object') {
      return [AsmServiceTools.toMessage(fallbackMessage, path)];
    }

    const payload = value as Record<string, unknown>;
    const messages: SolValidationMessage[] = [];

    const messageKeys = ['detail', 'message', 'error'];
    const consumedKeys = new Set<string>();
    for (const key of messageKeys) {
      const directValue = payload[key];
      if (typeof directValue === 'string' && directValue.trim() !== '') {
        messages.push(AsmServiceTools.toMessage(directValue, path));
        consumedKeys.add(key);
      }
    }

    for (const [key, nested] of Object.entries(payload)) {
      if (consumedKeys.has(key)) {
        continue;
      }

      const nextPath =
        key === 'non_field_errors' || key === '__all__' || key === 'errors' ? path : path ? `${path}.${key}` : key;
      messages.push(...AsmServiceTools.parseValue(nextPath, nested, fallbackMessage));
    }

    return messages;
  }

  private static toMessage(text: string, path: string | undefined): SolValidationMessage {
    return {
      ...(path ? { path } : {}),
      type: 'error',
      text: SolText.ofRaw(text),
    };
  }

  private static readStatus(error: unknown): number {
    if (error && typeof error === 'object') {
      const value = (error as Record<string, unknown>)['status'];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
    }
    return 500;
  }

  private static readMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (typeof error === 'string' && error.trim() !== '') {
      return error;
    }
    if (error && typeof error === 'object') {
      const value = (error as Record<string, unknown>)['message'];
      if (typeof value === 'string' && value.trim() !== '') {
        return value;
      }
    }
    return 'Request failed';
  }

  private static readPayload(error: unknown): unknown {
    if (error && typeof error === 'object' && 'error' in (error as Record<string, unknown>)) {
      return (error as Record<string, unknown>)['error'];
    }
    return error;
  }
}
